<template>
    <Card>
        <CardHeader>
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                    <div class="flex items-center gap-1.5">
                        <BarChart3 class="size-4 text-primary" />
                        <CardTitle class="text-sm font-medium text-primary">
                            Usage
                        </CardTitle>
                    </div>
                    <StatusBadge :status="status" />
                </div>

                <DropdownMenu :modal="false">
                    <DropdownMenuTrigger as-child>
                        <button
                            type="button"
                            class="flex items-center gap-0.5 pt-0.5 text-xs text-muted-foreground hover:text-foreground"
                        >
                            {{ activeMonthLabel }}
                            <ChevronDown class="size-3" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            v-for="entry in monthOptions"
                            :key="entry.key"
                            :class="{ 'font-medium': entry.key === activeMonth }"
                            class="text-xs"
                            @click="activeMonth = entry.key"
                        >
                            <Check v-if="entry.key === activeMonth" class="size-3.5" />
                            <span v-else class="size-3.5" />
                            {{ entry.label }}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </CardHeader>

        <CardContent>
            <div class="grid grid-cols-3 gap-6">
                <div>
                    <p class="text-3xl font-semibold text-new-neutral-900">
                        {{ activeUsage.activeUsers }}
                    </p>
                    <p class="mt-1 text-sm text-muted-foreground">Active users</p>
                    <p class="text-xs text-new-neutral-400">{{ activeMonthLabel }}</p>
                </div>
                <div>
                    <p class="text-3xl font-semibold text-new-neutral-900">
                        {{ activeUsage.newUsers }}
                    </p>
                    <p class="mt-1 text-sm text-muted-foreground">New users</p>
                    <p class="text-xs text-new-neutral-400">{{ activeMonthLabel }}</p>
                </div>
                <div>
                    <p class="text-3xl font-semibold text-new-neutral-900">
                        {{ formatBytes(activeUsage.dataUsage) }}
                    </p>
                    <p class="mt-1 text-sm text-muted-foreground">Usage</p>
                    <p class="text-xs text-new-neutral-400">{{ activeMonthLabel }}</p>
                </div>
            </div>

            <!-- Data Traffic Chart (selected month only) -->
            <div class="mt-6 pt-4">
                <p class="mb-2 text-xs font-medium text-muted-foreground">Data traffic</p>
                <div v-if="activeMonthTraffic.length > 0" class="relative h-40">
                    <canvas ref="chartCanvas"></canvas>
                </div>
                <div v-else class="flex h-40 items-center justify-center">
                    <div class="h-full w-full animate-pulse rounded bg-new-neutral-100"></div>
                </div>
            </div>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, nextTick, PropType } from 'vue';
import { BarChart3, ChevronDown, Check } from 'lucide-vue-next';
import Chart from 'chart.js/auto';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import StatusBadge from './StatusBadge.vue';

interface MonthlyUsage {
    key: string;
    label: string;
    activeUsers: number;
    newUsers: number;
    dataUsage: number;
}

const props = defineProps({
    usageByMonth: {
        type: Object as PropType<Record<string, MonthlyUsage>>,
        default: () => ({}),
    },
    dataTraffic: {
        type: Array as PropType<Array<{ day: string; bytes: number; month: string }>>,
        default: () => [],
    },
    lastUsedAt: {
        type: String as PropType<string | null>,
        default: null,
    },
});

// Month options sorted desc (newest first).
const monthOptions = computed<MonthlyUsage[]>(() =>
    Object.values(props.usageByMonth).sort((a, b) => (a.key < b.key ? 1 : -1)),
);

const defaultMonth = computed(() => monthOptions.value[0]?.key ?? '');
const activeMonth = ref<string>(defaultMonth.value);

watch(defaultMonth, (val) => {
    if (!activeMonth.value && val) activeMonth.value = val;
});

const activeUsage = computed<MonthlyUsage>(
    () =>
        props.usageByMonth[activeMonth.value] ?? {
            key: '',
            label: '',
            activeUsers: 0,
            newUsers: 0,
            dataUsage: 0,
        },
);

const activeMonthLabel = computed(() => activeUsage.value.label || 'This month');

const activeMonthTraffic = computed(() =>
    (props.dataTraffic || []).filter((d) => d.month === activeMonth.value),
);

const status = computed(() => (props.lastUsedAt ? 'active' : 'inactive'));

const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'kB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatBytesWithUnit = (bytes: number, decimals = 0): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'kB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
};

const chartCanvas = ref<HTMLCanvasElement>();
let chartInstance: Chart | null = null;

// Distinct bar colour per calendar month (1=Jan … 12=Dec). Chosen so adjacent
// months are visually different when the user scrolls through history.
const MONTH_BAR_COLORS: Record<number, string> = {
    1: '#A5B4FC',  // Jan – indigo
    2: '#F9A8D4',  // Feb – pink
    3: '#86EFAC',  // Mar – emerald
    4: '#D6BCFA',  // Apr – violet
    5: '#FDE68A',  // May – amber
    6: '#FCA5A5',  // Jun – red
    7: '#7DD3FC',  // Jul – sky
    8: '#FDBA74',  // Aug – orange
    9: '#C4B5FD',  // Sep – purple
    10: '#FCD34D', // Oct – yellow
    11: '#5EEAD4', // Nov – teal
    12: '#F472B6', // Dec – fuchsia
};

const activeBarColor = computed(() => {
    const monthNum = Number(activeMonth.value.split('-')[1] || 0);
    return MONTH_BAR_COLORS[monthNum] ?? '#D6BCFA';
});

const renderChart = () => {
    if (!chartCanvas.value || activeMonthTraffic.value.length === 0) return;

    if (chartInstance) {
        chartInstance.destroy();
    }

    const labels = activeMonthTraffic.value.map((d) => d.day);
    const data = activeMonthTraffic.value.map((d) => d.bytes);

    chartInstance = new Chart(chartCanvas.value, {
        type: 'bar',
        data: {
            labels,
            datasets: [
                {
                    data,
                    backgroundColor: activeBarColor.value,
                    barPercentage: 1,
                    categoryPercentage: 0.6,
                },
            ],
        },
        options: {
            maintainAspectRatio: false,
            interaction: { intersect: true, mode: 'index' },
            scales: {
                y: {
                    grid: { color: '#e6e8eb' },
                    ticks: {
                        callback: (value) => {
                            if (value !== 0) {
                                return formatBytesWithUnit(parseInt(value.toString()), 0);
                            }
                        },
                        maxTicksLimit: 5,
                        color: '#6B7280',
                        font: { size: 10 },
                    },
                },
                x: {
                    grid: { display: false },
                    ticks: {
                        color: '#6B7280',
                        font: { size: 10 },
                    },
                },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: '#252F3F',
                    caretSize: 0,
                    cornerRadius: 6,
                    padding: 6,
                    bodyFont: { size: 12 },
                    callbacks: {
                        label: (ctx) => formatBytesWithUnit(parseInt(ctx.raw?.toString() || '0'), 2),
                        title: () => '',
                    },
                },
            },
        },
    });
};

watch(
    activeMonthTraffic,
    async () => {
        await nextTick();
        renderChart();
    },
    { deep: true },
);

watch(activeMonth, async () => {
    await nextTick();
    renderChart();
});

onMounted(() => {
    if (activeMonthTraffic.value.length > 0) {
        renderChart();
    }
});
</script>
